import {
  BusTieStatusType,
  BusTieStatusTypeFormat,
  CircuitDeviceLinkType,
  CircuitType,
  CircuitTypeFormat,
  VoltageLevelTypeFormatList,
  VoltageLevelWithOutfourTypeFormatList,
} from '@/api/circuit';
import { getOuListOwned, OuListResponse } from '@/api/ou';
import { Col, FormInstance, Row } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { isNil } from 'lodash-es';
import {
  apiV2DistributionCabinetRoomCabinetListPost,
  apiV2ComprehensiveLabelListPost,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { Button, Form, Input, InputNumber, Select, TreeSelect, useUpdate } from '@maxtropy/components';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { NodeType, RoomItem } from '@/pages/DistributionCabinetManage/utils';
import styles from './index.module.scss';
import CabinetSwitch from './CabinetSwitch';

interface BasicInfoFormProps {
  form: FormInstance;
  isEdit?: boolean;
}

const BasicInfoForm: FC<BasicInfoFormProps> = props => {
  const { form, isEdit } = props;
  const [ouList, setOuList] = useState<OuListResponse[]>([]);
  const devices: any[] = Form.useWatch('devices', form);
  const distributionRoomId: any = Form.useWatch('distributionRoomId', form);
  const hasCreatePermission = useHasPermission(PermissionsType.B_CIRCUIT_LABEL_CEATE_V2);
  const [roomTree, setRoomTree] = useState<RoomItem[]>([]);
  const [treeData, setTreeData] = useState<RoomItem[]>([]);
  const [updateDistributionCabinet, updateDistributionCabinetFn] = useUpdate();
  const type: number = Form.useWatch('type', form);
  const voltageLevel: number = Form.useWatch('voltageLevel', form); // 电压等级
  const voltageLevelIn: number = Form.useWatch('voltageLevelIn', form); // 进线电压等级
  const ouIds: number = Form.useWatch('ouId', form); // 进线电压等级

  useEffect(() => {
    getOuListOwned().then(setOuList); // 所属运营单元下拉选择

    apiV2DistributionCabinetRoomCabinetListPost({ ouIds: [ouIds] }).then(res => {
      setTreeData(res.list ?? []);
      const filterNodesByType = (nodes: RoomItem[]): RoomItem[] => {
        if (!nodes) return [];

        return nodes
          .filter(node => node.type === NodeType.ROOM) // 只保留type=0的节点
          .map(node => ({
            ...node,
            title: node.name,
            value: node.id,
            children: filterNodesByType(node.children ?? []), // 递归处理子节点
          }));
      };

      const filteredData = filterNodesByType(res.list ?? []);
      setRoomTree(filteredData);
    });
  }, [updateDistributionCabinet, ouIds]);

  const ouOptions = useMemo(() => {
    if (ouList && ouList.length !== 0) {
      return ouList.map(i => ({ label: i.name, value: i.id }));
    }
  }, [ouList]);

  // 填写装机容量回填目标负载率
  const capacityChange = (v: number | null) => {
    if (v === 0) {
      form.setFieldsValue({
        targetLoadRate: '--',
      });
    } else {
      if (v && !isNil(form.getFieldValue('targetLoad'))) {
        const res = (form.getFieldValue('targetLoad') / v) * 100;
        form.setFieldsValue({
          targetLoadRate: Number(res.toFixed(2)),
        });
      } else {
        form.setFieldsValue({
          targetLoadRate: undefined,
        });
      }
    }
  };
  // 填写目标负载回填目标负载率
  const targetLoadChange = (v: number | null) => {
    if (form.getFieldValue('capacity') === 0) {
      form.setFieldsValue({
        targetLoadRate: '--',
      });
    } else {
      if (!v || !form.getFieldValue('capacity')) {
        form.setFieldsValue({
          targetLoadRate: undefined,
        });
      } else {
        const res = (v / form.getFieldValue('capacity')) * 100;
        form.setFieldsValue({
          targetLoadRate: Number(res.toFixed(2)),
        });
      }
    }
  };

  // 所属配电柜选项
  const distributionCabinetOptions = useMemo(() => {
    if (!distributionRoomId)
      return (treeData ?? [])
        .filter(k => k.type === NodeType.CABINET)
        .map(cabinet => ({
          label: cabinet.name,
          value: cabinet.id,
        }));

    // 查找匹配的节点
    const matchedNode = treeData.filter(item => item.id === distributionRoomId)?.[0];
    if (!matchedNode) return [];

    // 生成选项
    return (matchedNode?.children ?? [])
      ?.filter(k => k.type === NodeType.CABINET)
      .map(cabinet => ({
        label: cabinet.name,
        value: cabinet.id,
      }));
  }, [distributionRoomId, treeData]);

  console.log('distributionCabinetOptions', distributionCabinetOptions);

  const { data: labelList, refresh } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 0 }));

  return (
    <>
      <Row gutter={50}>
        <Col span={8}>
          <Form.Item
            name="name"
            label="回路名称"
            rules={[
              { required: true, message: '请输入回路名称' },
              { max: 100, message: '最多输入100个字符！' },
            ]}
          >
            <Input placeholder="请输入回路名称" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="ouId"
            label="所属运营单元"
            extra={isEdit && '更改所属运营单元前请解绑计量表和生产负载表。'}
            rules={[{ required: true, message: '请选择所属运营单元' }]}
          >
            <Select
              placeholder="请选择所属运营单元"
              options={ouOptions}
              showSearch
              optionFilterProp="label"
              onChange={() => {
                form.setFieldValue('distributionRoomId', undefined);
                form.setFieldValue('distributionCabinetId', undefined);
              }}
              disabled={
                isEdit
                  ? devices &&
                    (devices.map(i => i.deviceType).includes(CircuitDeviceLinkType.GAUGE) ||
                      devices.map(i => i.deviceType).includes(CircuitDeviceLinkType.TRANSFORMER))
                    ? true
                    : false
                  : false
              }
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="回路类型" name="type" rules={[{ required: true, message: '请选择回路类型' }]}>
            <Select
              placeholder="请选择"
              disabled={isEdit}
              onChange={() => {
                form.setFieldValue('voltageLevel', undefined);
                if (isEdit) {
                  form.setFieldsValue({
                    cabinetType: undefined,
                    switchType: undefined,
                    switchStatus: undefined,
                  });
                }
              }}
            >
              {Object.keys(CircuitTypeFormat).map(key => (
                <Select.Option key={key} value={Number(key)}>
                  {CircuitTypeFormat[key as unknown as CircuitType]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Form.Item noStyle dependencies={['type']}>
          {({ getFieldValue }) =>
            getFieldValue('type') === CircuitType.VOLTAGE_CONVERSION_CIRCUIT ||
            getFieldValue('type') === CircuitType.LEAD_WIRE_SWITCH_CIRCUIT ? (
              <Col span={8}>
                <Form.Item label="电压等级" name="voltageLevel" rules={[{ required: true, message: '请选择电压等级' }]}>
                  <Select
                    placeholder="请选择"
                    onChange={() => {
                      if (isEdit) {
                        form.setFieldsValue({
                          cabinetType: undefined,
                          switchType: undefined,
                          switchStatus: undefined,
                        });
                      }
                    }}
                  >
                    {VoltageLevelWithOutfourTypeFormatList().map(([value, label]) => {
                      return (
                        <Select.Option key={value} value={Number(value)}>
                          {label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            ) : (
              getFieldValue('type') !== CircuitType.TRANSFORMER_CIRCUIT && (
                <Col span={8}>
                  <Form.Item
                    label="电压等级"
                    name="voltageLevel"
                    rules={[{ required: true, message: '请选择电压等级' }]}
                  >
                    <Select
                      placeholder="请选择"
                      onChange={() => {
                        if (isEdit) {
                          console.log(111);
                          form.setFieldsValue({
                            cabinetType: undefined,
                            switchType: undefined,
                            switchStatus: undefined,
                          });
                        }
                      }}
                    >
                      {VoltageLevelTypeFormatList().map(([value, label]) => {
                        return (
                          <Select.Option key={value} value={Number(value)}>
                            {label}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              )
            )
          }
        </Form.Item>
        <Form.Item noStyle dependencies={['type']}>
          {({ getFieldValue }) =>
            getFieldValue('type') === CircuitType.TRANSFORMER_CIRCUIT && (
              <>
                <Col span={8}>
                  <Form.Item
                    label="进线电压等级"
                    name="voltageLevelIn"
                    rules={[{ required: true, message: '请选择进线电压等级' }]}
                  >
                    <Select
                      placeholder="请选择"
                      onChange={() => {
                        if (isEdit) {
                          form.setFieldsValue({
                            cabinetType: undefined,
                            switchType: undefined,
                            switchStatus: undefined,
                          });
                        }
                      }}
                    >
                      {VoltageLevelTypeFormatList().map(([value, label]) => {
                        return (
                          <Select.Option key={value} value={Number(value)}>
                            {label}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="出线电压等级"
                    name="voltageLevelOut"
                    rules={[{ required: true, message: '请选择出线电压等级' }]}
                  >
                    <Select placeholder="请选择">
                      {VoltageLevelTypeFormatList().map(([value, label]) => {
                        return (
                          <Select.Option key={value} value={Number(value)}>
                            {label}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )
          }
        </Form.Item>
        <Col span={8}>
          <Form.Item noStyle dependencies={['type']}>
            {({ getFieldValue }) => {
              // 只有电网计费回路、变压器回路中必填，其他回路为非必填；
              let isRequired = [CircuitType.TRANSFORMER_CIRCUIT, CircuitType.GRID_BILLING_CIRCUIT].includes(
                getFieldValue('type')
              );
              return (
                <Form.Item
                  label="装机容量"
                  name="capacity"
                  rules={[{ required: isRequired, message: '请输入装机容量' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    precision={0}
                    max={999999}
                    min={0}
                    addonAfter="kVA"
                    placeholder="请输入"
                    onChange={capacityChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="目标负载" name="targetLoad">
            <InputNumber
              style={{ width: '100%' }}
              addonAfter="kW"
              min={0}
              max={999999}
              precision={0}
              placeholder="请输入"
              onChange={targetLoadChange}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="目标负载率" name="targetLoadRate">
            <InputNumber
              disabled
              style={{ width: '100%' }}
              addonAfter="%"
              min={0}
              max={100}
              precision={2}
              placeholder="目标负载/装机容量"
            />
          </Form.Item>
        </Col>
        <Form.Item noStyle dependencies={['type']}>
          {({ getFieldValue }) =>
            getFieldValue('type') === CircuitType.BUS_COUPLER_CIRCUIT && (
              <Col span={8}>
                <Form.Item name="busTieStatus" label="母联状态" rules={[{ required: true, message: '请选择母联状态' }]}>
                  <Select placeholder="请选择">
                    {Object.keys(BusTieStatusTypeFormat).map(key => (
                      <Select.Option key={key} value={Number(key)}>
                        {BusTieStatusTypeFormat[key as unknown as BusTieStatusType]}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )
          }
        </Form.Item>
        <Col span={8}>
          <Form.Item label="所属站房" name="distributionRoomId">
            <TreeSelect
              treeData={roomTree}
              allowClear
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
              onChange={() => {
                form.setFieldValue('distributionCabinetId', undefined);
              }}
              placeholder="请选择"
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.baseFlex}>
          <Form.Item
            label="所属配电柜"
            name="distributionCabinetId"
            style={{ flex: 1 }}
            rules={[{ required: true, message: '请选择所属配电柜' }]}
          >
            <Select
              placeholder="请选择"
              allowClear
              showSearch
              optionFilterProp="label"
              options={distributionCabinetOptions}
            />
          </Form.Item>
          <Button onClick={() => updateDistributionCabinetFn()}>刷新</Button>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              window.open('/dc/circuit/distributionCabinetManage', '_blank');
            }}
          >
            新建配电柜 &gt;&gt;
          </Button>
        </Col>
        <Col span={8}>
          <Form.Item label="备注" name="remark" rules={[{ max: 150, message: '最多输入150个字符！' }]}>
            <Input placeholder="请输入" />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.baseFlex}>
          <Form.Item label="标签" name="labelIds" style={{ flex: 1 }}>
            <Select
              placeholder="请选择"
              options={labelList?.list?.map(item => ({ value: item.id, label: item.name }))}
              allowClear
              mode="multiple"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Button onClick={() => refresh()}>刷新</Button>
          {hasCreatePermission && (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                window.open('/dc/circuit/labelsManage?labelId=0', '_blank');
              }}
            >
              新建标签 &gt;&gt;
            </Button>
          )}
        </Col>
        <Col span={8}>
          <Form.Item
            label="回路为站房总开回路"
            name="roomIdList"
            style={{ flex: 1 }}
            tooltip="当前回路为某站房的总开回路，请在该字段中选择，若不为任何站房的总开，则无需选择"
          >
            <TreeSelect
              treeData={roomTree}
              allowClear
              treeDefaultExpandAll
              showSearch
              multiple
              treeNodeFilterProp="title"
              placeholder="请选择"
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="额定电流" name="ratedCurrent">
            <InputNumber placeholder="请输入" addonAfter="A" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        {/* 配电柜相关信息render */}
        <CabinetSwitch
          isEdit={isEdit}
          type={type}
          voltageLevel={voltageLevel}
          voltageLevelIn={voltageLevelIn}
          form={form}
        />
      </Row>
    </>
  );
};

export default BasicInfoForm;
